import expectWrongIllustration from "../assets/illustrations/guide/common-questions/expect-something-wrong.png";
import feelBehindIllustration from "../assets/illustrations/guide/common-questions/feel-behind-social.png";
import lonelyConnectedIllustration from "../assets/illustrations/guide/common-questions/lonely-but-connected.png";
import careerOverwhelmIllustration from "../assets/illustrations/guide/common-questions/career-choice-overwhelm.png";
import neverGoodEnoughIllustration from "../assets/illustrations/guide/common-questions/never-good-enough.png";
import emotionsQuicklyIllustration from "../assets/illustrations/guide/common-questions/emotions-too-quickly.png";
import cantFocusIllustration from "../assets/illustrations/guide/common-questions/cant-focus.png";
import exhaustedBeginningIllustration from "../assets/illustrations/guide/common-questions/exhausted-life-beginning.png";
import rejectionWorthIllustration from "../assets/illustrations/guide/common-questions/rejection-self-worth.png";
import askForHelpIllustration from "../assets/illustrations/guide/common-questions/hard-to-ask-for-help.png";

/**
 * Newer long-form common questions (hub list + detail pages).
 * Keep copy in CBT / decision-journal register — Ahavibes is a reflection tool, not treatment.
 */
export const COMMON_QUESTION_ARTICLES = [
  {
    id: "why-does-my-mind-always-expect-something-to-go-wrong",
    question: "Why does my mind always expect something to go wrong?",
    eyebrow: "Possibility is not the same as probability",
    pageTitle: "Why Does My Mind Always Expect Something to Go Wrong? | Ahavibes",
    metaDescription:
      "When your mind prepares for problems that have not happened, separate the event from the prediction. Examine the evidence before you treat a worst-case story as fact.",
    answerParagraphs: [
      "Sometimes your mind prepares for problems that have not happened. A delayed reply feels like rejection. A small mistake feels like the beginning of failure. Even when life is calm, you may find yourself waiting for something to go wrong.",
      "This can happen when your mind confuses possibility with probability. It creates future scenarios to protect you from surprise, but repeatedly preparing for imagined outcomes can leave you tense and mentally exhausted.",
    ],
    askYourself: [
      "What happened, and what am I predicting will happen next?",
      "What evidence supports this prediction?",
      "What would I do if the situation turned out better than expected?",
      "What is within my control right now?",
    ],
    tryAhavibes: "Separate the current event from the future your mind is creating.",
    ctaLabel: "Separate event from prediction",
    relatedGuides: [
      { path: "/mental-noise/what-is-mental-noise", label: "What Is Mental Noise" },
      { path: "/mental-noise/mental-noise-and-anxiety", label: "Mental Noise and Anxiety" },
    ],
    illustration: expectWrongIllustration,
    illustrationAlt:
      "Person looking ahead while translucent thought shapes of predicted problems loom in front of them.",
    keywords: [
      "expecting something to go wrong",
      "anxious thoughts",
      "worst-case thinking",
      "overthinking",
      "catastrophizing",
      "Ahavibes reflection",
    ],
  },
  {
    id: "why-do-i-feel-behind-every-time-i-open-social-media",
    question: "Why do I feel behind every time I open social media?",
    eyebrow: "Selected highlights are not a timeline",
    pageTitle: "Why Do I Feel Behind Every Time I Open Social Media? | Ahavibes",
    metaDescription:
      "Social media shows selected moments from other lives. Examine what you saw, what you made it mean about you, and whose timeline you are using to measure progress.",
    answerParagraphs: [
      "You open social media and see someone receiving a promotion, traveling, getting married, or launching a business. Nothing about your life changed — but within minutes, it can feel as though everyone is progressing except you.",
      "You are seeing selected moments from other people's lives while comparing them with your entire reality. The painful appraisal may not come from what they achieved. It may come from the meaning you attach to their achievement.",
    ],
    askYourself: [
      "What exactly did I see?",
      "What did I make their success mean about me?",
      "What progress in my own life am I overlooking?",
      "Whose timeline am I using to measure myself?",
      "What would progress look like for me this week?",
    ],
    tryAhavibes: "Examine the story that comparison created about your life.",
    ctaLabel: "Examine the comparison story",
    relatedGuides: [
      { path: "/better-decisions/assumptions-vs-facts", label: "Assumptions vs Facts" },
      { path: "/structured-thinking/event-vs-interpretation", label: "Event vs Interpretation" },
    ],
    illustration: feelBehindIllustration,
    illustrationAlt:
      "Person viewing idealized success silhouettes on a phone while their own path feels smaller.",
    keywords: [
      "social-media comparison",
      "feeling behind in life",
      "comparing yourself to others",
      "social comparison",
      "Ahavibes guide",
    ],
  },
  {
    id: "why-do-i-feel-lonely-even-when-im-always-connected",
    question: "Why do I feel lonely even when I'm always connected?",
    eyebrow: "Contact is not the same as connection",
    pageTitle: "Why Do I Feel Lonely Even When I'm Always Connected? | Ahavibes",
    metaDescription:
      "Frequent digital contact can still leave you lonely. Name whether you need company, understanding, reassurance, or belonging — then choose a clear next step.",
    answerParagraphs: [
      "You may receive messages, share videos, participate in group chats, and still feel that nobody truly knows what you need. Digital contact can create frequent interaction without providing meaningful connection.",
      "Loneliness is not always about being alone. Sometimes it comes from feeling misunderstood, unable to express what you genuinely need, or lacking a place where you belong. Understanding the kind of connection you are missing can help you choose a deliberate next step.",
    ],
    askYourself: [
      "Do I need company, understanding, reassurance, or belonging?",
      "With whom do I feel most able to be myself?",
      "What prevents me from saying how I really feel?",
      "What small act of connection could I initiate today?",
    ],
    tryAhavibes: "Name the connection you are missing before deciding how to seek it.",
    ctaLabel: "Name the missing connection",
    relatedGuides: [
      { path: "/emotional-awareness/emotional-awareness-explained", label: "Emotional Awareness Explained" },
      { path: "/emotional-awareness/naming-emotions", label: "Naming Emotions" },
    ],
    illustration: lonelyConnectedIllustration,
    illustrationAlt:
      "Person surrounded by glowing message bubbles yet still alone among distant figures.",
    keywords: [
      "lonely but connected",
      "loneliness while online",
      "feeling alone with friends",
      "digital loneliness",
      "Ahavibes reflection",
    ],
  },
  {
    id: "why-does-choosing-a-career-feel-so-overwhelming",
    question: "Why does choosing a career feel so overwhelming?",
    eyebrow: "You may need a next experiment, not a permanent answer",
    pageTitle: "Why Does Choosing a Career Feel So Overwhelming? | Ahavibes",
    metaDescription:
      "Career choice feels heavy when one decision is expected to satisfy income, identity, and purpose at once. Separate facts, expectations, and fears — then choose a reversible next step.",
    answerParagraphs: [
      "Choosing a career can feel like choosing your entire future. You may worry about selecting the wrong field, disappointing your family, losing financial stability, or watching others move ahead while you remain uncertain.",
      "The pressure becomes heavier when one decision is expected to satisfy every need: income, identity, security, interest, recognition, and purpose. You may not need a permanent answer. You may only need enough clarity to choose your next experiment.",
    ],
    askYourself: [
      "What decision do I actually need to make now?",
      "Which expectations are mine, and which came from other people?",
      "What am I afraid this decision will say about me?",
      "What could I test before making a long-term commitment?",
      "What is my next reversible step?",
    ],
    tryAhavibes: "Organize the facts, expectations, fears, and options surrounding your decision.",
    ctaLabel: "Organize the career decision",
    relatedGuides: [
      { path: "/better-decisions/clear-decision-making", label: "Clear Decision Making" },
      { path: "/better-decisions/decision-fatigue", label: "Decision Fatigue" },
    ],
    illustration: careerOverwhelmIllustration,
    illustrationAlt:
      "Person at a crossroads with many faint branching paths and career symbols creating overwhelm.",
    keywords: [
      "career uncertainty",
      "choosing a career",
      "overwhelmed by career choices",
      "career decision making",
      "Ahavibes",
    ],
  },
  {
    id: "why-do-i-never-feel-good-enough",
    question: "Why do I never feel good enough?",
    eyebrow: "A belief is not the same as evidence",
    pageTitle: "Why Do I Never Feel Good Enough? | Ahavibes",
    metaDescription:
      "“I’m not good enough” often operates as an interpretation, not an established fact. Examine when it started, whose standard it uses, and what evidence does not fit.",
    answerParagraphs: [
      "You accomplish something, but your mind immediately moves the standard. A good result could have been better. Praise feels undeserved. Someone else's success makes you question your own worth.",
      "\"I'm not good enough\" often operates as an interpretation rather than an established fact. When repeated frequently, it can influence which opportunities you pursue, how you receive feedback, and how much of your success you allow yourself to recognize.",
    ],
    askYourself: [
      "When did I start feeling that I was not enough?",
      "Enough for whom — and according to what standard?",
      "What evidence does not fit this belief?",
      "How has this belief influenced my choices?",
      "What would I attempt if I did not need to prove my worth?",
    ],
    tryAhavibes: "Examine the evidence behind the belief instead of automatically accepting it.",
    ctaLabel: "Test the belief against evidence",
    relatedGuides: [
      { path: "/structured-thinking/evidence-based-thinking", label: "Evidence-Based Thinking" },
      { path: "/structured-thinking/the-eier-framework", label: "The EIER Framework" },
    ],
    safetyNote:
      "Ahavibes is a structured reflection tool, not treatment or crisis care. If self-judgment is intense or persistent and you feel unable to cope, contact a qualified professional or local crisis service.",
    illustration: neverGoodEnoughIllustration,
    illustrationAlt:
      "Person holding an achievement while a translucent thought figure raises the standard higher.",
    keywords: [
      "why am I not good enough",
      "low self-worth",
      "feeling inadequate",
      "imposter thoughts",
      "evidence-based thinking",
    ],
  },
  {
    id: "why-do-my-emotions-become-too-much-so-quickly",
    question: "Why do my emotions become too much so quickly?",
    eyebrow: "Name the emotion before you choose the response",
    pageTitle: "Why Do My Emotions Become Too Much So Quickly? | Ahavibes",
    metaDescription:
      "A small comment can produce a strong reaction. Pause to name the emotion, what happened before it intensified, and what meaning you attached — then choose a response you respect.",
    answerParagraphs: [
      "A small comment, disagreement, or change of plan can sometimes produce a much stronger reaction than you expected. You may become angry, tense, tearful, or withdrawn before you fully understand what happened.",
      "The immediate event may have touched an older fear, expectation, or unmet need. Pausing to identify the emotion and its interpretation can create space between what you feel and what you do next.",
    ],
    askYourself: [
      "What emotion am I experiencing beneath my first reaction?",
      "What happened immediately before the feeling intensified?",
      "What meaning did I attach to that event?",
      "What does this emotion appear to be protecting?",
      "What response would I respect later?",
    ],
    tryAhavibes: "Name the emotion, examine its interpretation, and choose your response.",
    ctaLabel: "Name the emotion, then respond",
    relatedGuides: [
      { path: "/emotional-awareness/naming-emotions", label: "Naming Emotions" },
      { path: "/emotional-awareness/emotional-triggers", label: "Emotional Triggers" },
    ],
    safetyNote:
      "Ahavibes helps you examine interpretations and choose responses. It is not a substitute for professional care. If you feel unsafe or unable to cope, contact a qualified professional or local crisis service.",
    illustration: emotionsQuicklyIllustration,
    illustrationAlt:
      "Person with a small spark triggering a large wave of translucent intense emotion shapes.",
    keywords: [
      "overwhelming emotions",
      "intense emotional reactions",
      "naming emotions",
      "response vs reaction",
      "Ahavibes EIER",
    ],
  },
  {
    id: "why-cant-i-focus-even-when-i-know-what-i-need-to-do",
    question: "Why can't I focus even when I know what I need to do?",
    eyebrow: "Identify what attention is avoiding",
    pageTitle: "Why Can't I Focus Even When I Know What I Need to Do? | Ahavibes",
    metaDescription:
      "Difficulty focusing is not always laziness. Explore whether the task is unclear, uncomfortable, or competing with digital overload — then take the smallest clear action.",
    answerParagraphs: [
      "You know what needs to be done, but you continue scrolling, switching tasks, or thinking about everything except the task in front of you. This does not always mean you are lazy or undisciplined.",
      "Difficulty focusing can come from digital overstimulation, mental exhaustion, uncertainty, perfectionism, or discomfort with the task. Before forcing yourself to concentrate, identify what your attention may be avoiding.",
    ],
    askYourself: [
      "What happens internally when I try to begin?",
      "Is the task unclear, difficult, boring, or emotionally uncomfortable?",
      "Am I afraid of failing — or of being evaluated?",
      "What is the smallest clear action I can complete?",
      "What distraction can I remove for ten minutes?",
    ],
    tryAhavibes: "Explore what is competing for your attention before creating your next action.",
    ctaLabel: "Find what attention is avoiding",
    relatedGuides: [
      { path: "/mental-clarity/stress-reduces-clarity", label: "How Stress Reduces Mental Clarity" },
      { path: "/mental-noise/reducing-mental-noise", label: "Reducing Mental Noise" },
    ],
    illustration: cantFocusIllustration,
    illustrationAlt:
      "Person at a desk with a clear task while scrolling feeds and thought spirals pull attention away.",
    keywords: [
      "why can't I focus",
      "difficulty concentrating",
      "digital overload",
      "task avoidance",
      "mental clarity",
    ],
  },
  {
    id: "why-am-i-already-exhausted-when-my-life-is-just-beginning",
    question: "Why am I already exhausted when my life is just beginning?",
    eyebrow: "Unmade decisions also consume energy",
    pageTitle: "Why Am I Already Exhausted When My Life Is Just Beginning? | Ahavibes",
    metaDescription:
      "Exhaustion is not only from completed work. Unmade decisions, unfinished conversations, and constant self-monitoring create an invisible mental load. Offload what requires attention.",
    answerParagraphs: [
      "You may feel tired before the day starts — even when you believe you have not accomplished enough to justify feeling exhausted. School, work, finances, family expectations, career uncertainty, and maintaining an online presence can create a mental load that other people cannot see.",
      "Exhaustion is not only produced by completed work. Unmade decisions, suppressed emotions, unfinished conversations, and constant self-monitoring also consume energy.",
    ],
    askYourself: [
      "What am I carrying mentally, even when I am resting?",
      "Which expectations are placing the most pressure on me?",
      "What am I continuing only because I feel guilty stopping?",
      "What kind of rest or support do I actually need?",
      "What is one responsibility I can reduce, postpone, or discuss?",
    ],
    tryAhavibes: "Offload what your mind has been carrying and identify what requires attention.",
    ctaLabel: "Offload the invisible mental load",
    relatedGuides: [
      { path: "/mental-clarity/stress-reduces-clarity", label: "How Stress Reduces Mental Clarity" },
      { path: "/guide/common-questions/why-am-i-mentally-exhausted", label: "Why Am I Mentally Exhausted?" },
    ],
    safetyNote:
      "Ahavibes is a structured reflection tool for examining mental load, not a medical or burnout treatment. If exhaustion includes despair or thoughts of self-harm, seek immediate professional or crisis support.",
    illustration: exhaustedBeginningIllustration,
    illustrationAlt:
      "Tired young adult carrying translucent weight shapes representing expectations and unfinished loops.",
    keywords: [
      "mentally exhausted young adult",
      "tired all the time",
      "invisible mental load",
      "open loops",
      "Ahavibes",
    ],
  },
  {
    id: "why-does-rejection-feel-like-proof-that-im-not-enough",
    question: "Why does rejection feel like proof that I'm not enough?",
    eyebrow: "One outcome is not a verdict on identity",
    pageTitle: "Why Does Rejection Feel Like Proof That I'm Not Enough? | Ahavibes",
    metaDescription:
      "Rejection provides information about one interaction or decision — not a complete evaluation of your identity. Separate what happened from what you concluded about yourself.",
    answerParagraphs: [
      "A rejected application, unanswered message, or relationship ending can feel larger than the event itself. Instead of thinking, \"This opportunity did not work out,\" your mind may conclude, \"There is something wrong with me.\"",
      "Rejection provides information about one interaction or decision. It does not provide a complete evaluation of your identity. Separating the event from its personal meaning can help you learn without turning disappointment into self-judgment.",
    ],
    askYourself: [
      "What exactly was rejected?",
      "What am I making this rejection mean about me?",
      "What factors might have existed outside my control?",
      "What useful information can I take from the experience?",
      "What would I do next if rejection were not a verdict on my worth?",
    ],
    tryAhavibes: "Separate what happened from what you concluded about yourself.",
    ctaLabel: "Separate rejection from identity",
    relatedGuides: [
      { path: "/structured-thinking/event-vs-interpretation", label: "Event vs Interpretation" },
      { path: "/structured-thinking/respond-vs-react", label: "Respond vs React" },
    ],
    safetyNote:
      "Ahavibes helps you examine interpretations after rejection. It is not therapy. If rejection triggers despair or self-harm thoughts, contact a qualified professional or local crisis service.",
    illustration: rejectionWorthIllustration,
    illustrationAlt:
      "Person facing a closed door while a thought figure turns the event into a judgment about their worth.",
    keywords: [
      "fear of rejection",
      "rejection and self-worth",
      "why rejection hurts",
      "event vs interpretation",
      "Ahavibes",
    ],
  },
  {
    id: "why-is-it-so-hard-to-ask-for-help",
    question: "Why is it so hard to ask for help?",
    eyebrow: "Start with one honest sentence to one safe person",
    pageTitle: "Why Is It So Hard to Ask for Help? | Ahavibes",
    metaDescription:
      "Asking for help requires understanding what you need, whom you trust, and how you want to communicate. Organize what you are experiencing before you share it.",
    answerParagraphs: [
      "You may want support but hesitate to ask because you fear being judged, misunderstood, dismissed, or becoming a burden. You might also believe that capable people should be able to manage everything themselves.",
      "Asking for help requires more than courage. It requires understanding what you need, whom you trust, and how you want to communicate it. You do not have to tell everyone everything. You can begin with one honest sentence to one safe person.",
    ],
    askYourself: [
      "What am I afraid will happen if I ask for help?",
      "What kind of support do I need — listening, advice, practical assistance, or professional care?",
      "Who has previously responded to me with respect?",
      "What boundaries would make the conversation feel safer?",
      "What is one sentence I could use to begin?",
    ],
    tryAhavibes: "Organize what you are experiencing before sharing it with someone you trust.",
    ctaLabel: "Organize before you ask",
    relatedGuides: [
      { path: "/structured-thinking/thinking-frameworks", label: "Thinking Frameworks" },
      { path: "/guide/common-questions/how-do-i-organize-my-thoughts", label: "How Do I Organize My Thoughts?" },
    ],
    safetyNote:
      "Ahavibes is a structured reflection tool to organize what you want to share — not a replacement for professional care. If you need crisis support, contact local emergency services or a crisis helpline.",
    illustration: askForHelpIllustration,
    illustrationAlt:
      "Person wanting to reach toward another while translucent fear shapes hold their hand back.",
    keywords: [
      "why is it hard to ask for help",
      "afraid to seek support",
      "asking for support",
      "organize your thoughts",
      "Ahavibes",
    ],
  },
];
